/**
 * Contenido al pie de cada herramienta: como funciona, pasos, FAQ y enlaces internos.
 * Lo renderiza ToolContent, montado una sola vez desde UserLayout.
 *
 * Reglas que sigue este archivo:
 *
 *  1. Las cifras entre llaves salen de tool-counts.json, que se genera en cada build
 *     desde los mismos arrays que alimentan las calculadoras. Nada de numeros a mano:
 *     si mañana entran artefactos nuevos, el texto acompaña.
 *  2. Se describe lo que la herramienta hace, no formulas del juego que no esten en
 *     el codigo. Afirmar costos o multiplicadores sin respaldo en src/data seria
 *     exactamente el contenido inventado que hay que evitar.
 *  3. Cada entrada enlaza a 2-3 herramientas relacionadas. Ademas de ayudar al
 *     usuario, le da al rastreador caminos entre paginas: 20 paginas que solo cuelgan
 *     del menu lateral se leen como un directorio, no como un sitio.
 */
import conteos from './tool-counts.json'

const n = (dataset, clave, respaldo = 0) => conteos?.[dataset]?.[clave] ?? respaldo

/*
  Esta constante tenia 44 palabras y se servia como respuesta de FAQ en 12 paginas:
  528 palabras identicas repartidas por el sitio, mas la pregunta que la
  acompañaba. La guia de AdSense pide justo lo contrario —"try to minimize
  repeating long segments of text in different pages of your site... include a very
  brief summary and then link to a page with more details"— asi que la explicacion
  larga vive ahora una sola vez, en /about/#data, y el pie de cada herramienta
  enlaza ahi con una linea.

  Se deja la constante por si alguna entrada necesita el texto completo, pero no la
  usa ninguna FAQ.
*/
const DESCARGO_DATOS =
  'Players compile the data by hand and check it against the game, so it can lag a patch. Corrections go through the contact page.'

export const CONTENIDO_HERRAMIENTAS = {
  // La home es la pagina que mas mira un revisor y la que mas trafico recibe
  // (2.606 sesiones en 30 dias). Renderizaba 421 palabras: el parrafo de bienvenida
  // mas los nombres de las tarjetas. Aca va lo que el sitio es, para quien, y de
  // donde salen los datos — las cuatro preguntas que alguien se hace al llegar.
  '/': {
    tituloComoFunciona: 'What WAO Tools is',
    comoFunciona: [
      'WAO Tools is an independent set of calculators, planners and references for War and Order. It exists because the game shows you one decision at a time — the next building level, the next research node, the next artifact upgrade — and never the whole curve. Most expensive mistakes in this game are not tactical; they are a resource committed to the wrong upgrade because the alternative was never priced.',
      `Every tool here works from data compiled by players and checked against the game: ${n('building', 'buildingList', 440)} building upgrade steps, ${n('artifacts', 'artifactsList', 35)} artifacts, ${n('equipment', 'equipmentList', 78)} equipment pieces, ${n('packs', 'packs', 89)} packs, ${n('realms', 'mergeList', 214)} realm merges, and the research, totem, garden and beast tables behind them. Nothing is scraped from your account, because the game exposes no way to do that.`,
      'That design has a consequence worth stating plainly: you enter your own figures, and everything you enter stays in your browser. There is no account to create, no login, and no server holding your castle data. It also means nothing syncs between your phone and your desktop, and clearing site data clears your saved setups.'
    ],
    pasos: [
      "If you are new to the game, start with the Beginner's Guide rather than a calculator.",
      'If you are planning upgrades, the Planner sums a whole queue and projects when it finishes.',
      'If you need one number now, the Buildings List and the Database are straight lookups.',
      'If you are optimising a march, Artifacts, Equipment, Jewels and Troops Setup work together.'
    ],
    faq: [
      {
        p: 'Is WAO Tools free?',
        r: 'Yes, all of it, with no account and no paid tier. The site is funded by advertising and by voluntary contributions listed on the contributors page. No tool is gated.'
      },
      {
        p: 'Is this an official War and Order site?',
        r: 'No. WAO Tools is an independent fan project with no affiliation to War and Order or its publishers. Everything here is built and maintained by players.'
      },
      {
        p: 'Where does the game data come from?',
        r: 'Players compile it by hand and check it against the game. It is not scraped and not official, so it can lag a patch. Every tool page carries a note about this, and the contact page is where corrections go — several tables here were fixed because a user reported a mismatch.'
      },
      {
        p: 'Do I need to create an account?',
        r: 'No. Nothing here requires signing in. Your inputs are saved in your own browser using local storage, which is why there is nothing to log in to and nothing of yours on a server.'
      },
      {
        p: 'Can the tools read my game account?',
        r: 'No. War and Order provides no public API, so there is no way for any external tool to read your account. Anything claiming otherwise should be treated with suspicion. Here you type your own numbers in.'
      }
    ],
    relacionadas: [
      {
        href: '/guides/what-the-cost-tables-actually-say/',
        titulo: 'What the Cost Tables Actually Say',
        nota: 'the whole upgrade table added up: where the resources actually go'
      },
      { href: '/planner/', titulo: 'Building and Technology Planner', nota: 'the tool most players use most' },
      { href: '/guides/', titulo: 'All guides', nota: 'written strategy, not calculators' }
    ]
  },

  '/building': {
    tituloComoFunciona: 'About the War and Order buildings list',
    comoFunciona: [
      `This page is a flat reference table of every upgrade step in the game — ${n('building', 'buildingList', 440)} rows in total, covering the buildings themselves plus the High College and Mystic College research steps. Each row shows one building at one level, with the Azurite, Food, Wood, Stone and Iron that step consumes. Nothing is hidden behind a calculation: what you see is the raw cost table, so you can compare two upgrades side by side before deciding which one to queue.`,
      'The reason a flat table beats an in-game menu is that the game only shows you the next level of the building you are looking at. It will not tell you that the step after it costs three times as much, and it will not let you compare a wall upgrade against a barracks upgrade. Here you can sort any column and see the whole curve at once, which is where most planning mistakes get caught.'
    ],
    pasos: [
      'Use the Data Filter box to search by building name, or by a resource if you are hunting for a specific cost bracket.',
      'Click a column header to sort. Sorting by a resource column shows you the cheapest and most expensive steps for that resource.',
      'Note the level where costs jump. Those are the steps worth saving for rather than starting on impulse.',
      'Move to the Planner when you want to queue several of these upgrades and see the combined total.'
    ],
    faq: [
      {
        p: 'Why does the list start at level 30 for most buildings?',
        r: 'The table focuses on the levels where planning actually matters. Early levels are cheap enough that you upgrade them as resources arrive, without needing a spreadsheet. From the thirties onward a single step can cost more than a week of income, and that is where a reference table earns its keep.'
      },
      {
        p: 'Do these costs include event discounts or buffs?',
        r: 'No. The figures are base costs. Construction speed-ups, alliance help and event discounts all apply on top, and they vary per account, so folding them in would make the table wrong for everyone. Treat these numbers as the ceiling and anything you save as a bonus.'
      },
      {
        p: 'What are the sacred levels shown on some buildings?',
        r: 'Some buildings continue past their normal cap into sacred levels, tracked separately in the data. Where a building has them, the row carries the sacred level alongside the regular level so you can see both progressions in the same view.'
      }
    ],
    relacionadas: [
      {
        href: '/planner/',
        titulo: 'Building and Technology Planner',
        nota: 'queue several upgrades and see the combined cost'
      },
      { href: '/azurite/', titulo: 'Azurite Calculator', nota: 'work out how long the Azurite for a step will take' },
      {
        href: '/guides/what-the-cost-tables-actually-say/',
        titulo: 'What the Cost Tables Actually Say',
        nota: 'this same table summed: wood is 46% of the bill and the Castle is 38%'
      }
    ]
  },

  '/planner': {
    tituloComoFunciona: 'About the Building and Technology Planner',
    comoFunciona: [
      'The Planner is where you assemble a shopping list instead of pricing one item at a time. You pick the buildings you intend to upgrade and the research you intend to run, and it sums the resources across the whole queue. The Azurite Calculator is built into the same page, so once it knows your daily Azurite income it can also tell you the date that queue finishes.',
      `It draws on the same ${n('building', 'buildingList', 440)}-row cost table as the buildings list, plus the research requirements, so the totals match the reference data rather than an estimate. There are ${n('building', 'itemTemplates', 25)} saved templates available, which is useful when you want to keep a "next 30 days" plan and a "post-event" plan side by side without rebuilding either from scratch.`
    ],
    pasos: [
      'Set your castle level first — several requirement checks depend on it.',
      'Add the buildings you want to upgrade, then the technologies you want to research.',
      'Enter your daily Azurite income in the Azurite Calculator section so the completion dates become meaningful.',
      'Save the plan as a template before you change anything, so you can compare two versions.'
    ],
    faq: [
      {
        p: 'What does the projected completion date assume?',
        r: 'It assumes your current daily income holds steady from today. It is a straight division of what you still need by what you earn per day, not a forecast. If your income changes — a new event, a finished farm upgrade — re-enter it and the date moves with it.'
      },
      {
        p: 'Why does the plan show a dash instead of a date?',
        r: 'That happens when the daily total is zero or has not been entered yet. Dividing by zero has no meaningful answer, so the tool shows a dash rather than an invented date.'
      },
      {
        p: 'Does the Planner know what I have already built?',
        r: 'Not automatically — the game exposes no API for that. You tell it your current levels, and it plans forward from there. Saving a template after each real upgrade is the fastest way to keep it honest.'
      },
      {
        p: 'Is my plan stored anywhere I could lose it?',
        r: 'Plans are saved in your own browser, not on a server. That means no account is needed, but it also means clearing site data wipes them, and a plan saved on your phone will not appear on your desktop.'
      }
    ],
    relacionadas: [
      { href: '/building/', titulo: 'Buildings List', nota: 'the underlying cost table for every step' },
      { href: '/azurite/', titulo: 'Azurite Calculator', nota: 'the same projection as a standalone tool' },
      {
        href: '/guides/what-the-cost-tables-actually-say/',
        titulo: 'What the Cost Tables Actually Say',
        nota: 'why resources and azurite are two separate budgets'
      }
    ]
  },

  '/azurite': {
    tituloComoFunciona: 'About the Azurite Calculator',
    comoFunciona: [
      'Azurite is the resource that gates most late-game upgrades, and the question it always raises is not "how much" but "how long". This calculator answers the second one. You enter what a target needs, what you already hold and what you gain per day, and it returns the shortfall and the date you close it.',
      'The projection is deliberately simple: remaining need divided by daily income, rounded up to whole days, counted forward from today. There is no attempt to model events or spending spikes, because those are unpredictable per account and a confident-looking wrong date is worse than an honest rough one. Where the inputs do not produce a real number, the tool shows a dash instead of guessing.'
    ],
    pasos: [
      'Enter the Azurite the upgrade requires — the Buildings List has the figure for any building step.',
      'Enter how much Azurite you currently hold.',
      'Enter your realistic daily income, averaged over a normal week rather than your best day.',
      'Read the shortfall and the projected date. Re-run it with a higher income figure to see what extra farming buys you.'
    ],
    faq: [
      {
        p: 'What counts as daily Azurite income?',
        r: 'Everything that reliably lands in your account in a day: mine output, gathering runs, alliance sources and routine event rewards. Use a weekly average divided by seven. One exceptional day will make every date optimistic.'
      },
      {
        p: 'Why does my date look further out than I expected?',
        r: 'Usually because the income figure entered was a good day rather than a typical one. The arithmetic is a plain division, so the output is only as honest as the input.'
      },
      {
        p: 'Can it plan several upgrades at once?',
        r: 'This page handles one target. For a full queue with several buildings and research steps summed together, use the Planner, which has the same calculator built in.'
      },
      {
        p: 'Does it account for Azurite spent elsewhere?',
        r: 'No. It assumes everything you earn goes toward the target you entered. If you are also feeding research or troops, add a margin or lower the income figure to match what you actually set aside.'
      }
    ],
    relacionadas: [
      {
        href: '/planner/',
        titulo: 'Building and Technology Planner',
        nota: 'the same projection across a whole queue'
      },
      { href: '/building/', titulo: 'Buildings List', nota: 'look up the Azurite cost of any step' },
      {
        href: '/guides/what-the-cost-tables-actually-say/',
        titulo: 'What the Cost Tables Actually Say',
        nota: '303 of the 322 research steps cost azurite and nothing else'
      }
    ]
  },

  '/artifacts': {
    tituloComoFunciona: 'About the artifacts configurator',
    comoFunciona: [
      `The configurator holds ${n('artifacts', 'artifactsList', 35)} artifacts and lets you build a loadout across ${n('artifacts', 'forceSlots', 5)} force slots plus ${n('artifacts', 'crownSlots', 3)} crown slots, then read the combined stat effect. Because you are choosing from the full catalogue rather than only what you own, you can test a setup before committing shards to it.`,
      `There are ${n('artifacts', 'artifactsTemplates', 25)} saved template slots, and an experience and shards calculator alongside the configurator. The reason to model this outside the game is that artifact materials are slow to accumulate and effectively unrecoverable once spent, so the cost of a wrong choice is measured in weeks.`
    ],
    pasos: [
      'Fill your force slots with the artifacts you already own, to establish the baseline you are trying to beat.',
      'Swap one slot at a time and watch which stat moves. Changing several at once hides which piece did the work.',
      'Use the experience and shards calculator to price the upgrade path for the piece you settled on.',
      'Save the setup as a template so you can come back and compare after the next event.'
    ],
    faq: [
      {
        p: 'What is the difference between force slots and crown slots?',
        r: 'Force slots are configured per troop type — Infantry, Cavalry, Archers and Mages — so their bonuses apply to that force. Crown slots are separate and tracked apart in the data. The configurator keeps them in distinct sections for that reason.'
      },
      {
        p: 'Should I spread upgrades across artifacts or focus one?',
        r: 'That depends on what you are optimising and no calculator settles it for you. What the tool does give you is the honest cost of each option, so the trade-off is visible before you spend rather than after.'
      },
      {
        p: 'Does the configurator know which artifacts I own?',
        r: 'No, and that is intentional — the whole catalogue stays selectable so you can model a piece you are considering chasing. Your saved templates are the record of what you actually run.'
      }
    ],
    relacionadas: [
      { href: '/equipment/', titulo: 'Equipment and Enhancements', nota: 'the other half of a march loadout' },
      { href: '/troops/', titulo: 'Troops Setup', nota: 'save the formation these artifacts buff' },
      { href: '/jewels/', titulo: 'Jewel Upgrade Calculator', nota: 'price the jewels in the same gear slots' }
    ]
  },

  '/troops': {
    tituloComoFunciona: 'About Troops Setup and formation layouts',
    comoFunciona: [
      `This tool records march formations so you stop rebuilding them from memory. It holds ${n('troops', 'troopsTemplates', 53)} template slots, with Even and Odd layout variants, which is what you need when a rally and a solo hit call for different arrangements and you want both saved.`,
      'The value is comparison rather than calculation. Sitting two formations next to each other makes the composition difference obvious in a way that reading them off separate in-game screens does not, and a saved template survives the moment when a rally is starting and there is no time to think.'
    ],
    pasos: [
      'Build the formation you currently run and save it. That is your baseline.',
      'Duplicate it, change one thing, and save the variant under a name you will recognise later.',
      'Use the Even and Odd layouts when a setup depends on position parity.',
      'Review after events. Formations that worked one season often do not the next.'
    ],
    faq: [
      {
        p: 'What do the Even and Odd layouts mean?',
        r: 'They are two separate position arrangements you can save independently, for cases where a setup depends on which positions are filled. Keeping them apart means one does not overwrite the other.'
      },
      {
        p: 'Does it calculate battle outcomes?',
        r: 'No. It records and compares formations. Combat in War and Order depends on variables no external tool can see — the defender, buffs live at that moment, timing — so anything claiming to predict a result would be guessing.'
      },
      {
        p: 'Where are my saved formations stored?',
        r: 'In your browser, on the device you saved them on. No account, but also no sync: save them again on your phone if you want them there.'
      },
      {
        p: 'How many formations can I keep?',
        r: `The tool ships with ${n('troops', 'troopsTemplates', 53)} template slots, which is more than most players fill.`
      }
    ],
    relacionadas: [
      { href: '/artifacts/', titulo: 'Artifacts Configuration', nota: 'the buffs that apply to these troops' },
      { href: '/speeds/', titulo: 'Recruitment Speed Calculator', nota: 'how long these troops take to train' },
      {
        href: '/guides/batch-healing-troops/',
        titulo: 'Batch Healing Troops',
        nota: 'getting them back without burning speed-ups'
      }
    ]
  },

  '/jewels': {
    tituloComoFunciona: 'About the Jewel Upgrade Calculator',
    comoFunciona: [
      `The calculator covers ${n('jewels', 'jewelsData', 6)} jewels against a ${n('jewels', 'levelCosts', 40)}-row cost table. You set a current level and a target level, and it returns the materials the whole path consumes rather than just the next step.`,
      'Reading the total instead of the next step is the point. Jewel upgrades are cheap early and steep later, so a target that looks two levels away can cost more than everything you have spent on that jewel so far. Seeing the full figure before starting is what stops a half-finished upgrade path.'
    ],
    pasos: [
      'Pick the jewel slot you are working on.',
      'Enter its current level, then the level you are aiming for.',
      'Read the material totals for the full path, not just the next step.',
      'Compare against another jewel before committing — the cheaper path is not always the obvious one.'
    ],
    faq: [
      {
        p: 'Does it show the cost per level or the total?',
        r: 'The total for the range you asked about. That is the number that matters when deciding whether to start, because stopping halfway leaves the materials spent and the bonus unearned.'
      },
      {
        p: 'Are Legendary, Epic and Rare jewels costed differently?',
        r: 'Yes, the cost tables differ by jewel, which is why you select the specific jewel rather than a generic slot. The data carries each one separately.'
      },
      {
        p: 'Does it include special effects and set bonuses?',
        r: 'The page documents the special effects and bonuses alongside the calculator so you can weigh them, but the calculator itself prices materials. What a bonus is worth to your account is a judgement call, not arithmetic.'
      }
    ],
    relacionadas: [
      { href: '/equipment/', titulo: 'Equipment and Enhancements', nota: 'the gear these jewels socket into' },
      { href: '/artifacts/', titulo: 'Artifacts Configuration', nota: 'the other stat source on a march' },
      { href: '/database/', titulo: 'Database', nota: 'Obsidian and VIP reference tables' }
    ]
  },

  '/college': {
    tituloComoFunciona: 'About the High College Simulator',
    comoFunciona: [
      `The simulator covers ${n('college', 'highCollegeOptions', 31)} High College research options. You configure a research path and it returns the resource requirement for that branch, so you can price two competing paths before committing to either.`,
      'High College research is a long commitment, and the in-game interface shows you one node at a time. Pricing a whole branch up front changes the decision: a path that looks affordable node by node can turn out to cost more than the alternative that reaches the same place.'
    ],
    pasos: [
      'Select the branch you are considering.',
      'Configure the levels you want to reach on each node in that branch.',
      'Read the total requirement, then repeat for the competing branch.',
      'Compare the two totals against your actual income before starting either.'
    ],
    faq: [
      {
        p: 'Does the simulator change anything in my game account?',
        r: 'No. Nothing here touches your account — there is no login and no connection to the game. It is a planning surface: you model a path, read the cost, and then decide what to do in game.'
      },
      {
        p: 'Why price a whole branch instead of the next node?',
        r: 'Because the next node is the number the game already gives you. The decision that costs people weeks is choosing a branch, and that needs the total.'
      },
      {
        p: 'How does this differ from the Mystic College tool?',
        r: 'They are separate research systems with separate data, so they have separate tools. High College options and Mystic College paths are tracked independently.'
      }
    ],
    relacionadas: [
      { href: '/mystic/', titulo: 'Mystic College Simulator', nota: 'the other research system' },
      { href: '/planner/', titulo: 'Building and Technology Planner', nota: 'fold research into a full upgrade queue' },
      {
        href: '/guides/what-the-cost-tables-actually-say/',
        titulo: 'What the Cost Tables Actually Say',
        nota: 'what the 135 college steps cost once you add them up'
      },
      {
        href: '/guides/high-college-priced-in-azurite/',
        titulo: 'The High College, Priced in Azurite',
        nota: '378,710 azurite to max all 31 researches, and the 29x gap between them'
      }
    ]
  },

  '/mystic': {
    tituloComoFunciona: 'About the Mystic College Simulator',
    comoFunciona: [
      `The Mystic College tool works from ${n('college', 'mysticBuilding', 130)} building entries and their matching requirement rows, across ${n('college', 'mysticSlots', 5)} slots. You select the abilities you intend to raise and it returns what that path needs.`,
      'Mystic abilities interact with building requirements, which is the part that catches people out: an ability you can afford may still be locked behind a building you have not raised. Modelling the two together shows the real blocker rather than just the resource cost.'
    ],
    pasos: [
      'Choose the abilities you want to raise, and to what level.',
      'Check the building requirements the tool surfaces alongside the cost.',
      'If a requirement is unmet, price that building step too — the Buildings List has it.',
      'Compare against a second ability path before spending.'
    ],
    faq: [
      {
        p: 'Why does the tool show building requirements?',
        r: 'Because ability levels are gated behind them. Showing the resource cost without the requirement would let you plan a path you cannot start, which is the most common planning mistake in this system.'
      },
      {
        p: 'Can I model an ability I have not unlocked?',
        r: 'Yes. The full set stays selectable so you can plan ahead rather than only price what is already available.'
      },
      {
        p: 'Does it save my configuration?',
        r: 'Configurations are stored in your browser on the device you used. There is no account, so nothing syncs between devices and clearing site data clears them.'
      }
    ],
    relacionadas: [
      { href: '/college/', titulo: 'High College Simulator', nota: 'the other research system' },
      { href: '/building/', titulo: 'Buildings List', nota: 'price the buildings these abilities require' },
      { href: '/azurite/', titulo: 'Azurite Calculator', nota: 'how long the Azurite will take to gather' }
    ]
  },

  '/equipment': {
    tituloComoFunciona: 'About Equipment and Enhancements',
    comoFunciona: [
      `The page combines an equipment reference of ${n('equipment', 'equipmentList', 78)} pieces across ${n('equipment', 'equipmentSlots', 6)} slots with an enhancement simulator covering ${n('equipment', 'enhancementsData', 48)} enhancement rows, plus ${n('equipment', 'equipmentTemplates', 29)} template slots for saving loadouts.`,
      'Equipment and enhancement are two decisions that get made together and priced separately, which is how people end up with a strong piece they cannot afford to enhance. Having both in one view means the total cost of reaching a stat target is visible before the first material is spent.'
    ],
    pasos: [
      'Fill the slots with what you currently run, to fix your baseline.',
      'Use the enhancement simulator to price raising one piece rather than replacing it.',
      'Compare that against the cost of the better piece plus its enhancement from scratch.',
      'Save the winning loadout as a template.'
    ],
    faq: [
      {
        p: 'Is it better to enhance what I have or chase a better piece?',
        r: 'The tool prices both so you can see the gap, but the answer depends on what materials you already hold and how close the better piece is. What it removes is the guesswork about the cost.'
      },
      {
        p: 'What is the quality list for?',
        r: 'Equipment comes in different qualities and the data tracks them separately, because the same slot behaves differently depending on quality. The simulator keeps that distinction rather than averaging it away.'
      },
      {
        p: 'Does the simulator include Obsidian levels?',
        r: 'Obsidian enhancement level data lives on the Database page, which is the reference for those tables. This page covers equipment and its enhancements.'
      }
    ],
    relacionadas: [
      { href: '/jewels/', titulo: 'Jewel Upgrade Calculator', nota: 'the jewels that socket into this gear' },
      { href: '/artifacts/', titulo: 'Artifacts Configuration', nota: 'the other stat source on a march' },
      { href: '/database/', titulo: 'Database', nota: 'Obsidian and VIP reference tables' }
    ]
  },

  '/packs': {
    tituloComoFunciona: 'About the Pack Tracker',
    comoFunciona: [
      `The tracker holds ${n('packs', 'packs', 89)} packs and ${n('packs', 'itemList', 39)} item types, organised by the day of the week they appear. You log what you bought and it keeps the record, so "did I already buy that one" stops being a guess.`,
      'The comparison side matters more than the logging. Packs are priced against contents that are hard to value at a glance, and two packs at the same price can differ substantially in what they actually contain. Laying them out together turns an impulse decision into a comparison.'
    ],
    pasos: [
      'Pick the weekday to see which packs are on offer then.',
      'Compare contents across packs at similar prices before buying any of them.',
      'Log what you purchased so the record is there next cycle.',
      'Use Save to keep the record in your browser, and Delete to clear it.'
    ],
    faq: [
      {
        p: 'Does the tracker know real prices in my currency?',
        r: 'Pack pricing varies by region and store, so the tool does not claim a price in your currency. It tracks what packs contain and what you logged, which is the part that is the same for everyone.'
      },
      {
        p: 'Why organise packs by weekday?',
        r: 'Because availability rotates, and knowing what is coming tomorrow changes whether you buy today. The weekday view is there to answer that.'
      },
      {
        p: 'Is my purchase history sent anywhere?',
        r: 'No. It is stored in your own browser and never leaves your device. There is no account and no server-side record of what you logged.'
      },
      {
        p: 'What happens if I clear my browser data?',
        r: 'The log is gone — it lives only in that browser. There is no backup on our side to restore it from.'
      }
    ],
    relacionadas: [
      { href: '/database/', titulo: 'Database', nota: 'VIP and SVIP benefit tables' },
      { href: '/planner/', titulo: 'Building and Technology Planner', nota: 'what the resources would actually fund' },
      {
        href: '/guides/what-the-packs-actually-cost/',
        titulo: 'What the Packs Actually Cost',
        nota: 'every tier divided by what it contains — the cheapest tier wins 93% of the time'
      }
    ]
  },

  '/totem': {
    tituloComoFunciona: 'About the totems tool',
    comoFunciona: [
      `The tool covers ${n('totem', 'totemList', 8)} totems with their stat tables, a stones and scrolls calculator, and ${n('totem', 'totemTemplates', 10)} template slots. You set totem levels and read the resulting stat bonuses for your troops.`,
      'The stats comparator is the part worth using deliberately. Totem bonuses are small individually and substantial in aggregate, which makes them easy to underrate and hard to compare by eye. Setting two configurations side by side shows which one actually moves the stat you care about.'
    ],
    pasos: [
      'Enter your current totem levels to establish the baseline.',
      'Raise one totem at a time in the comparator and note which stat responds.',
      'Use the stones and scrolls calculator to price the levels you want.',
      'Save the configuration as a template before experimenting further.'
    ],
    faq: [
      {
        p: 'What does the stats comparator compare?',
        r: 'Two totem configurations against each other, so you can see the stat delta rather than reading two separate absolute figures and doing the subtraction yourself.'
      },
      {
        p: 'What do stones and scrolls do here?',
        r: 'They are the upgrade materials, and the calculator prices how many a target level needs. Materials are the constraint on totems, so the cost side is the planning question.'
      },
      {
        p: 'Do totem bonuses apply to all troop types?',
        r: 'The stat tables in the data are tracked per totem, and the tool shows what each configuration produces rather than assuming a blanket effect. Read the output for your specific setup.'
      }
    ],
    relacionadas: [
      { href: '/troops/', titulo: 'Troops Setup', nota: 'the formations these bonuses apply to' },
      { href: '/garden/', titulo: 'Garden', nota: 'decoration and statue bonuses' },
      { href: '/colossus/', titulo: 'Colossus', nota: 'another long-run stat investment' }
    ]
  },

  '/garden': {
    tituloComoFunciona: 'About the garden reference',
    comoFunciona: [
      `The reference covers ${n('garden', 'gardenDecorations', 37)} decorations, ${n('garden', 'gardenDecorationSets', 5)} sets, ${n('garden', 'gardenStatues', 6)} statues, ${n('garden', 'gardenBeastSkins', 6)} beast skins and ${n('garden', 'gardenIllusions', 4)} illusions, with the stats each one provides.`,
      'Sets are the reason this needs a table. Individual decorations give modest bonuses, but a completed set behaves differently, and the game does not make it easy to see how close you are to finishing one or what finishing it would be worth. Laid out together, "which set should I finish first" becomes answerable.'
    ],
    pasos: [
      'Browse the decorations list to see what each piece contributes on its own.',
      'Switch to sets to see which ones you are closest to completing.',
      'Check statues and skins separately — they are tracked apart from decorations.',
      'Decide which set to finish based on the bonus, not on how many pieces are left.'
    ],
    faq: [
      {
        p: 'Are set bonuses worth chasing over individual pieces?',
        r: 'Sometimes, and the table is there so you can check rather than assume. A set that needs two more pieces for a small bonus may be worse value than one strong individual decoration.'
      },
      {
        p: 'Do beast skins affect stats or only appearance?',
        r: 'Beast skins are tracked in the data with their own entries alongside decorations and statues. Read the entry for the specific skin rather than assuming they are cosmetic.'
      },
      {
        p: 'What are illusions?',
        r: 'A separate category in the garden data, kept apart from decorations, statues and skins because they behave as their own group.'
      }
    ],
    relacionadas: [
      { href: '/totem/', titulo: 'Totems', nota: 'another aggregate stat source' },
      { href: '/beast/', titulo: 'Beast', nota: 'beast talents and skills' },
      { href: '/database/', titulo: 'Database', nota: 'general reference tables' }
    ]
  },

  '/colossus': {
    tituloComoFunciona: 'About the Colossus Simulator',
    comoFunciona: [
      `The simulator works across ${n('colossus', 'colossusExp', 25)} experience levels and ${n('colossus', 'colossusSlots', 2)} slot groups, with ${n('colossus', 'colossusLevelTemplates', 6)} templates. You set crystal and relic limits, position troops, and configure ability slots to see what a build costs before committing.`,
      'Colossus materials are slow and the build is hard to reverse, so this is a place where modelling first pays for itself. Setting your real crystal and relic limits constrains the simulation to builds you could actually reach, rather than ones that look good on paper.'
    ],
    pasos: [
      'Enter your crystal and relic limits so the simulation stays realistic.',
      'Position your troops, then configure the ability slots.',
      'Read the resource cost of the build you assembled.',
      'Save it before trying an alternative, so you can compare rather than overwrite.'
    ],
    faq: [
      {
        p: 'What do the crystal and relic limits do?',
        r: 'They bound the simulation to what your account can support. Without them you can design a build you have no path to, which makes the output interesting but useless.'
      },
      {
        p: 'Does the simulator predict combat performance?',
        r: 'No. It prices builds and shows stat configurations. Combat outcomes depend on the opponent and on buffs live at that moment, none of which an external tool can see.'
      },
      {
        p: 'Are my saved builds kept anywhere?',
        r: 'In your browser only. No account, no sync, and clearing site data removes them.'
      }
    ],
    relacionadas: [
      { href: '/totem/', titulo: 'Totems', nota: 'another long-run stat investment' },
      { href: '/troops/', titulo: 'Troops Setup', nota: 'the formations this build supports' }
    ]
  },

  '/beast': {
    tituloComoFunciona: 'About the beast tools',
    comoFunciona: [
      `The page holds a talents simulator, a skills simulator and an experience chart, covering ${n('beast', 'beastList', 4)} beasts with ${n('beast', 'beastSkillsTemplates', 13)} skill templates and ${n('beast', 'dataOptions', 28)} configurable options.`,
      'Talents and skills are separate systems that get planned together, and the experience chart is what ties them to a timeline. Modelling a build before spending matters because beast resources accumulate slowly and respecs are not free.'
    ],
    pasos: [
      'Start with the talents simulator to settle the talent spread.',
      'Move to the skills simulator and configure skills against that spread.',
      'Check the experience chart to see what level the build needs.',
      'Save the configuration as a template before trying a variant.'
    ],
    faq: [
      {
        p: 'What is the difference between talents and skills?',
        r: 'They are separate systems with separate data, which is why there are two simulators. The tool keeps them apart rather than merging them into one view that would blur which one you are changing.'
      },
      {
        p: 'What is the experience chart for?',
        r: 'It maps levels to experience requirements, so a build you designed can be checked against the level it actually needs before you start feeding resources into it.'
      },
      {
        p: 'Can I model a beast I do not have?',
        r: 'Yes. All beasts in the data stay selectable, so you can plan for one you are working toward.'
      }
    ],
    relacionadas: [
      { href: '/garden/', titulo: 'Garden', nota: 'beast skins and decoration bonuses' },
      { href: '/totem/', titulo: 'Totems', nota: 'stat bonuses for your troops' },
      {
        href: '/guides/auto-attack-monsters-all-day/',
        titulo: 'Auto-Attacking Monsters All Day',
        nota: 'the beast passives that setup depends on'
      },
      {
        href: '/guides/does-your-beast-choice-change-the-cost/',
        titulo: 'Does Your Beast Choice Change the Cost?',
        nota: 'all four beasts cost exactly 267,705 — the difference is three talents'
      }
    ]
  },

  '/realms': {
    tituloComoFunciona: 'About the realms reference',
    comoFunciona: [
      `The reference tracks ${n('realms', 'mergeList', 214)} realm merges across ${n('realms', 'globeList', 19)} globes. It is a historical record plus upcoming schedules, which is the information that is hardest to find anywhere else and matters most when planning around a merge.`,
      'A merge changes the competitive picture of a realm overnight, and knowing the history of which realms merged when tells you a lot about the realm you are in or considering. The game does not surface that history, which is why it is compiled here.'
    ],
    pasos: [
      'Find your realm in the merge list to see its history.',
      'Check the globe it belongs to for related realms.',
      'Look at upcoming merges to see what is scheduled.',
      'Use the history of similar realms to anticipate what a merge tends to change.'
    ],
    faq: [
      {
        p: 'How far back does the merge history go?',
        r: `The list currently holds ${n('realms', 'mergeList', 214)} merge records. It is compiled by players over time rather than pulled from an official source, so it grows as merges happen and get reported.`
      },
      {
        p: 'Are upcoming merges official announcements?',
        r: 'They are what players have gathered and reported. Treat scheduled entries as the community record, not as a publisher announcement, and confirm in game before acting on a date.'
      },
      {
        p: 'What is a globe?',
        r: 'A grouping of realms. The list tracks which globe a realm belongs to, because merges tend to happen within a grouping and that context explains a lot of the pattern.'
      },
      {
        p: 'My realm is missing or wrong. Can that be fixed?',
        r: 'Yes — that is exactly the kind of correction the contact page is for. The record is only as good as what players report.'
      }
    ],
    relacionadas: [
      { href: '/shields/', titulo: 'Shield Timer and Tracker', nota: 'tracking shields across realms' },
      {
        href: '/guides/batch-healing-troops/',
        titulo: 'Batch Healing Troops',
        nota: 'alliance help is the currency after a merge fight'
      }
    ]
  },

  '/shields': {
    tituloComoFunciona: 'About the Shield Timer and Tracker',
    comoFunciona: [
      'The tracker records when shields go up and when they come down, across more than one realm at a time. That is the whole job: a shield expiring unnoticed is one of the most expensive mistakes in the game, and it happens because the information lives in a screen you are not looking at.',
      'Tracking several realms together is the part the game does not help with. If you run or coordinate more than one account, the timings that matter are the ones you are not currently logged into, and a single list beats checking each in turn.'
    ],
    pasos: [
      'Add each realm you need to track.',
      'Record when the shield went up and its duration.',
      'Check the list rather than the game when you want to know what is expiring.',
      'Update it when you re-shield, so the record stays worth trusting.'
    ],
    faq: [
      {
        p: 'Does it send me a notification when a shield is about to expire?',
        r: 'No. It is a record you check, not an alarm — there is no account and no server, so there is nowhere to send a notification from. Pair it with a phone reminder if you need to be told.'
      },
      {
        p: 'Can it read my shield status from the game?',
        r: 'No. There is no API for that, so the entries are the ones you record. Nothing here connects to your account.'
      },
      {
        p: 'Why track shields outside the game at all?',
        r: 'Because the game shows you one realm at a time, and the shield you forget is always the one you are not looking at. A single list across realms is the point.'
      },
      {
        p: 'Where is the data stored?',
        r: 'In your browser, on that device. No account, no sync, and clearing site data clears it.'
      }
    ],
    relacionadas: [
      { href: '/realms/', titulo: 'Realms', nota: 'merge history for the realms you track' },
      {
        href: '/guides/batch-healing-troops/',
        titulo: 'Batch Healing Troops',
        nota: 'spending the alliance help limit while you sit behind a shield'
      }
    ]
  },

  '/speeds': {
    tituloComoFunciona: 'About the Recruitment Speed Calculator',
    comoFunciona: [
      `The calculator turns your training speed bonuses into an actual recruitment time. You enter your speed buffs, capacity and troop type, and it returns how long the batch takes — with ${n('speeds', 'recruitmentTemplates', 11)} templates for saving the setups you use regularly.`,
      'Speed bonuses are expressed as percentages that stack, and percentages are hard to reason about in the head. The difference between two buff configurations only becomes concrete once it is expressed in hours, which is what this converts them into.'
    ],
    pasos: [
      'Enter your current training speed bonuses.',
      'Set the capacity and the troop type you are training.',
      'Read the resulting time, then change one buff to see what it is worth in hours.',
      'Save the configurations you use often as templates.'
    ],
    faq: [
      {
        p: 'Which bonuses should I include?',
        r: 'The training speed bonuses that are actually active for you when you queue troops. Including a buff you only have during events will make every estimate optimistic.'
      },
      {
        p: 'Does it account for speed-up items?',
        r: 'It calculates the recruitment time from your standing buffs. Speed-ups are applied on top and depend on what you hold, so they are a separate decision rather than part of the estimate.'
      },
      {
        p: 'Why does troop type change the time?',
        r: 'Different troops have different base training requirements, tracked separately in the data. Averaging them would make the answer wrong for every specific case.'
      }
    ],
    relacionadas: [
      { href: '/troops/', titulo: 'Troops Setup', nota: 'the formations you are filling' },
      { href: '/building/', titulo: 'Buildings List', nota: 'barracks upgrades that raise capacity' },
      {
        href: '/guides/batch-healing-troops/',
        titulo: 'Batch Healing Troops',
        nota: 'healing without spending these at all'
      }
    ]
  },

  '/minitools': {
    tituloComoFunciona: 'About the mini tools',
    comoFunciona: [
      'The mini tools are the small conversions that do not justify a page of their own but come up often enough to be annoying — Angel Dust conversion and requirements chief among them. They are grouped here so they are one click away rather than something you work out by hand each time.',
      'The reason they exist as a group is that each one is a two-line calculation you would otherwise do in your head and get wrong occasionally. Having them written down once removes a small recurring source of error.'
    ],
    pasos: [
      'Pick the conversion you need from the group.',
      'Enter your figures.',
      'Read the result — these are single-step conversions, nothing is hidden.'
    ],
    faq: [
      {
        p: 'What is the Angel Dust calculator for?',
        r: 'Working out conversion amounts and requirements, so you know what a target costs before starting rather than partway through.'
      },
      {
        p: 'Will more mini tools be added?',
        r: 'They get added when a calculation comes up often enough to be worth it. The contact page is where suggestions go, and several tools on this site started there.'
      },
      {
        p: 'Do these save anything?',
        r: 'No. They are stateless single-step conversions — enter numbers, read the result. Nothing to save and nothing stored.'
      }
    ],
    relacionadas: [
      { href: '/database/', titulo: 'Database', nota: 'reference tables for lookups' },
      { href: '/azurite/', titulo: 'Azurite Calculator', nota: 'the larger resource projection' },
      { href: '/guides/', titulo: 'All guides', nota: 'the written side of the site' }
    ]
  },

  '/database': {
    tituloComoFunciona: 'About the reference database',
    comoFunciona: [
      'This is the lookup page: Obsidian enhancement level data, VIP and SVIP level benefits, and the additional tables that do not belong to any single calculator. It exists so figures you need occasionally are somewhere findable rather than scattered.',
      'The tables are searchable, which is the actual feature. A reference table you have to scroll is barely better than the game menu it replaced; one you can search answers the question in a few seconds.'
    ],
    pasos: [
      'Use the search box rather than scrolling — it filters as you type.',
      'Switch between tables using the tabs.',
      'For anything tied to a specific calculation, the dedicated tool will be more useful than the raw table.'
    ],
    faq: [
      {
        p: 'What do the VIP and SVIP tables show?',
        r: 'The benefits at each level, so you can see what the next level actually unlocks before deciding whether it is worth reaching.'
      },
      {
        p: 'Where does the Obsidian data belong?',
        r: 'It is enhancement level reference data, which is why it sits here rather than in the Equipment simulator. The simulator handles the configuration; this page holds the lookup.'
      },
      {
        p: 'Why is some data here instead of in a tool?',
        r: 'Because not every table needs a calculator on top of it. When a lookup is the whole job, a searchable table is the better answer.'
      }
    ],
    relacionadas: [
      {
        href: '/equipment/',
        titulo: 'Equipment and Enhancements',
        nota: 'the simulator that uses the enhancement data'
      },
      { href: '/packs/', titulo: 'Pack Tracker', nota: 'weighing VIP against pack value' },
      { href: '/minitools/', titulo: 'Mini Tools', nota: 'quick conversions' }
    ]
  },

  '/dashboard': {
    tituloComoFunciona: 'About the castle dashboard',
    comoFunciona: [
      'The dashboard is the overview: your castle details and progress in one place, assembled from what you have entered across the other tools. It is the answer to "where am I actually at" without opening five pages.',
      'It reads from the same browser-stored configuration the other tools write to, so it reflects what you told them rather than what is live in your account. Keeping those entries current is what makes this page worth looking at.'
    ],
    pasos: [
      'Enter your castle details so the overview has something to show.',
      'Fill in the tools you use — the dashboard reflects what they hold.',
      'Come back after each real upgrade to keep the picture accurate.'
    ],
    faq: [
      {
        p: 'Does the dashboard read my live account?',
        r: 'No. There is no connection to the game and no API to read from. It shows what you have entered in this browser, which is why keeping it updated matters.'
      },
      {
        p: 'Why is my dashboard empty?',
        r: 'Because nothing has been entered yet in this browser, or site data was cleared. Fill in a tool or two and the overview populates.'
      },
      {
        p: 'Does my data leave my device?',
        r: 'No. Everything is stored locally in your browser. There is no account and nothing is transmitted to a server.'
      }
    ],
    relacionadas: [
      { href: '/planner/', titulo: 'Building and Technology Planner', nota: 'plan the next steps from here' },
      { href: '/building/', titulo: 'Buildings List', nota: 'look up what a step costs' },
      {
        href: '/guides/what-the-cost-tables-actually-say/',
        titulo: 'What the Cost Tables Actually Say',
        nota: 'what the numbers on this page add up to'
      }
    ]
  }
}
